// animation handled by Vaul Drawer; respects prefers-reduced-motion
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { X, Pencil } from "lucide-react";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@akuna/ui";
import { cn } from "@/lib/utils";
import { formatDateBR } from "@/lib/date-utils";
import { getStatusBadgeStyle } from "@/lib/utils/clinic-appointment-status";
import type { AppointmentDetailsController } from "@/components/calendar/useAppointmentDetailsController";
import { AppointmentPaymentCard } from "./AppointmentPaymentCard";
import { MobileAppointmentEditView } from "./MobileAppointmentEditView";
import { AppointmentStatusCard } from "./AppointmentStatusCard";
import { AppointmentStartAttendanceCard } from "./AppointmentStartAttendanceCard";
import { AppointmentPatientCard } from "./AppointmentPatientCard";
import { AppointmentProfessionalCard } from "./AppointmentProfessionalCard";
import { AppointmentNotesCard } from "./AppointmentNotesCard";
import { AppointmentBelongingsCard } from "./AppointmentBelongingsCard";
import { AppointmentItemsCard } from "./AppointmentItemsCard";
import { AppointmentPackagesCard } from "./AppointmentPackagesCard";
import { AppointmentEventsCard } from "./AppointmentEventsCard";

export function MobileAppointmentDetails({ ctrl }: { ctrl: AppointmentDetailsController }) {
  const { t } = useTranslation(["calendar", "common"]);
  const { appointment, open, onOpenChange, isEditing, handleEditClick, displayStatus, sidePanelStatuses, ownerName, activePetName } = ctrl;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        className={cn(
          "h-[100dvh] max-h-[100dvh] rounded-none border-0 bg-background-app p-0",
          "flex flex-col focus:outline-none",
        )}
      >
        {/* Header */}
        <header className="shrink-0 border-b border-border/40 bg-background-app/90 px-4 pb-3 pt-[max(env(safe-area-inset-top),1rem)] backdrop-blur">
          <div className="flex items-start gap-2.5">
            <button
              type="button"
              aria-label={t("common:close")}
              onClick={() => onOpenChange(false)}
              className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border/40 bg-card shadow-sm"
            >
              <X className="size-[18px] text-foreground" />
            </button>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="truncate text-[19px] font-extrabold leading-tight tracking-tight">
                {ownerName ? (
                  <>
                    {ownerName} <span className="text-muted-foreground">—</span> {activePetName}
                  </>
                ) : (
                  activePetName || t("calendar:appointmentDetails.appointmentNotFound")
                )}
              </div>
              {appointment?.starts_at ? (
                <div className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                  {formatDateBR(appointment.starts_at)} ·{" "}
                  {format(new Date(appointment.starts_at), "HH:mm", { locale: ptBR })}
                  {appointment.ends_at
                    ? ` – ${format(new Date(appointment.ends_at), "HH:mm", { locale: ptBR })}`
                    : ""}
                </div>
              ) : null}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleEditClick}
              disabled={!appointment || isEditing}
              className="h-[34px] shrink-0 rounded-full bg-card"
            >
              <Pencil className="size-3.5" /> {t("common:edit")}
            </Button>
          </div>
          {appointment ? (
            <div className="mt-2.5 flex items-center gap-1.5">
              {appointment.clinic_appointment_type?.name ? (
                <Badge
                  variant="outline"
                  className="rounded-full border-0 text-white"
                  style={{ backgroundColor: appointment.clinic_appointment_type.color ?? "#6366f1" }}
                >
                  {appointment.clinic_appointment_type.name}
                </Badge>
              ) : null}
              <Badge
                className="rounded-full border-0"
                style={getStatusBadgeStyle(displayStatus, sidePanelStatuses)}
              >
                {sidePanelStatuses.find((s) => s.name === displayStatus)?.display_name ?? displayStatus}
              </Badge>
            </div>
          ) : null}
        </header>

        {/* Body */}
        {isEditing ? (
          <MobileAppointmentEditView ctrl={ctrl} />
        ) : (
          <>
            <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 pb-[max(env(safe-area-inset-bottom),1.25rem)] pt-3.5">
              <AppointmentStatusCard ctrl={ctrl} />
              <AppointmentStartAttendanceCard ctrl={ctrl} />
              <AppointmentPatientCard ctrl={ctrl} />
              <AppointmentProfessionalCard ctrl={ctrl} />
              <AppointmentNotesCard ctrl={ctrl} />
              <AppointmentBelongingsCard ctrl={ctrl} />
              <AppointmentItemsCard ctrl={ctrl} />
              <AppointmentPackagesCard ctrl={ctrl} />
              <AppointmentPaymentCard ctrl={ctrl} />
              <AppointmentEventsCard ctrl={ctrl} />
            </main>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}
